import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";

export default function Register() {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const inviteToken = searchParams.get('invite') ?? undefined;
  const [f, setF] = useState({ 
    parentName: "", 
    email: "", 
    password: "",
    confirmPassword: "",
    phone: "", 
    childName: "", 
    ageGroup: "U11", 
    role: "parent" 
  });
  const [msg, setMsg] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  function validateForm() {
    const newErrors: Record<string, string> = {};
    
    if (!f.parentName.trim()) newErrors.parentName = "Name is required";
    if (!f.email.trim()) newErrors.email = "Email is required";
    if (!f.password) newErrors.password = "Password is required";
    if (!f.confirmPassword) newErrors.confirmPassword = "Please confirm password";
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (f.email && !emailRegex.test(f.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (f.password && f.confirmPassword && f.password !== f.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    if (f.password && f.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function onSubmit(e: React.FormEvent) { 
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setMsg(null);
    
    try {
      const r = await fetch("/api/registration", { 
        method: "POST", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify({ ...f, ...(inviteToken ? { inviteToken } : {}) }),
        credentials: "include"
      });
      
      if (r.ok) {
        // Parents are auto-activated server-side (server/routes/registration.ts).
        // Don't promise a verification email — none is sent right now (SendGrid
        // not yet wired in production). Tell the truth and bounce them to login.
        setMsg(inviteToken
          ? "Account created! Your child has been linked. Redirecting to sign in…"
          : "Account created! Redirecting you to sign in…");
        setF({ parentName: "", email: "", password: "", confirmPassword: "", phone: "", childName: "", ageGroup: "U11", role: "parent" });
        setTimeout(() => navigate("/login"), 1500);
      } else {
        const errorData = await r.json().catch(() => ({}));
        setMsg(errorData.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setMsg("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 px-4 py-8" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold text-gray-900" data-testid="heading-register">
              Parent Registration
            </h1>
            <p className="text-gray-600">Create your parent account to manage your kids' sessions and payments</p>
          </div>

          {msg && (
            <div
              className={`px-4 py-3 rounded-lg text-sm ${
                msg.includes("Thank you")
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
              }`}
              data-testid="reg-result"
            >
              {msg}
            </div>
          )}

          {/* Google Sign-In — provisions as parent. Existing email parents
              get linked, not duplicated. Errors surface in the shared `msg`
              banner above (red variant). */}
          <GoogleSignInButton onError={(text: string) => setMsg(text)} />

          {/* OR divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">OR</span>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="form-register">
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Parent Information</h2>
              
              <div className="space-y-2">
                <label htmlFor="parentName" className="block text-sm font-medium text-gray-700">
                  Your Full Name <span className="text-red-500">*</span>
                </label>
                <input 
                  id="parentName"
                  placeholder="Enter your full name" 
                  value={f.parentName} 
                  onChange={e => setF({...f, parentName: e.target.value})} 
                  data-testid="register-name" 
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400 ${
                    errors.parentName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.parentName && (
                  <p className="text-red-500 text-sm mt-1">{errors.parentName}</p>
                )}
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Login Details</h2>
              
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input 
                  id="email"
                  placeholder="you@example.com" 
                  type="email" 
                  value={f.email} 
                  onChange={e => setF({...f, email: e.target.value})} 
                  data-testid="register-email" 
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password <span className="text-red-500">*</span>
                </label>
                <input 
                  id="password"
                  placeholder="Create a password" 
                  type="password" 
                  value={f.password} 
                  onChange={e => setF({...f, password: e.target.value})} 
                  data-testid="register-password" 
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400 ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                <p className="text-xs text-gray-500">Must be at least 8 characters</p>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input 
                  id="confirmPassword"
                  placeholder="Re-enter your password" 
                  type="password" 
                  value={f.confirmPassword} 
                  onChange={e => setF({...f, confirmPassword: e.target.value})} 
                  data-testid="register-confirm-password" 
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <button 
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" 
              data-testid="register-submit"
              disabled={submitting}
            >
              {submitting ? "Creating Account..." : "Create Parent Account"}
            </button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Already registered?</span>
            </div>
          </div>

          <div className="text-center">
            <a 
              href="/login" 
              className="text-blue-600 hover:text-blue-800 font-medium underline" 
              data-testid="link-login"
            >
              Sign in here
            </a>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          © 2024 Legacy Cricket Academy. All rights reserved.
        </p>
      </div>
    </div>
  );
}
